/* =============================================================
   TanStack Query wrappers for everything that talks to Supabase.
   Co-locating queries and mutations here gives us a single index
   of every server interaction in the app — useful for review.
   ============================================================ */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import type { Database, OrgRole } from './database.types';
import {
  allStageAverages,
  overallAverage,
  overallZoneCounts,
  type Responses,
} from './psychSafety';

// ============================================================
// Workspaces (orgs the current user belongs to)
// ============================================================

export interface MyWorkspace {
  id: string;
  name: string;
  kind: string | null;
  role: OrgRole;
  joined_at: string;
}

/**
 * Fetches all workspaces the current user is a member of, with the
 * user's role in each. Sorted by most recently joined first.
 */
export function useMyWorkspaces(userId: string | undefined) {
  return useQuery<MyWorkspace[]>({
    queryKey: ['my-workspaces', userId],
    enabled: !!userId,
    queryFn: async () => {
      // Read from org_members; embed the org row. RLS gates
      // ALL org_members reads to memberships of orgs the user
      // is in — but we still filter by user_id to get just the
      // user's own role rather than every member of every org.
      const { data, error } = await supabase
        .from('org_members')
        .select('role, joined_at, orgs!inner(id, name, kind)')
        .eq('user_id', userId!)
        .order('joined_at', { ascending: false });

      if (error) throw error;

      type Row = {
        role: OrgRole;
        joined_at: string;
        orgs: { id: string; name: string; kind: string | null };
      };

      return (data as unknown as Row[]).map((r) => ({
        id: r.orgs.id,
        name: r.orgs.name,
        kind: r.orgs.kind,
        role: r.role,
        joined_at: r.joined_at,
      }));
    },
  });
}

/**
 * Creates a new workspace and atomically inserts the caller as 'owner'.
 * Server-side RPC ensures the two writes happen in one transaction.
 */
export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { name: string; kind: string | null }) => {
      const { data, error } = await supabase.rpc('create_org_with_owner', {
        p_name: args.name,
        p_kind: args.kind,
      });
      if (error) throw error;
      return data as Database['public']['Tables']['orgs']['Row'];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-workspaces'] });
    },
  });
}

// ============================================================
// Workspace detail (single org + members list)
// ============================================================

export interface WorkspaceMember {
  user_id: string;
  role: OrgRole;
  joined_at: string;
  full_name: string | null;
}

export function useWorkspace(orgId: string | undefined) {
  return useQuery({
    queryKey: ['workspace', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orgs')
        .select('*')
        .eq('id', orgId!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

// ============================================================
// Psychological Safety Assessment — drafts + responses
// ============================================================

export interface AssessmentDraft {
  responses: Responses;
  stage_idx: number;
  updated_at: string;
}

/** The current user's in-progress draft, or null if none. */
export function useAssessmentDraft(userId: string | undefined) {
  return useQuery<AssessmentDraft | null>({
    queryKey: ['assessment-draft', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assessment_drafts')
        .select('responses, stage_idx, updated_at')
        .eq('user_id', userId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        responses: (data.responses ?? {}) as Responses,
        stage_idx: data.stage_idx,
        updated_at: data.updated_at,
      };
    },
  });
}

/** Upsert the current user's draft. */
export function useUpsertAssessmentDraft(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (draft: { responses: Responses; stage_idx: number }) => {
      if (!userId) throw new Error('not signed in');
      const { error } = await supabase
        .from('assessment_drafts')
        .upsert({
          user_id: userId,
          responses: draft.responses,
          stage_idx: draft.stage_idx,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-draft', userId] });
    },
  });
}

export function useDeleteAssessmentDraft(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('not signed in');
      const { error } = await supabase
        .from('assessment_drafts')
        .delete()
        .eq('user_id', userId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-draft', userId] });
    },
  });
}

export interface CompletedResponse {
  id: string;
  responses: Responses;
  overall_score: number | null;
  stage_scores: Record<string, number> | null;
  zone_counts: Record<string, number> | null;
  completed_at: string;
}

/** The most recent completed assessment for the current user (or null). */
export function useLatestAssessmentResponse(userId: string | undefined) {
  return useQuery<CompletedResponse | null>({
    queryKey: ['assessment-latest', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('id, responses, overall_score, stage_scores, zone_counts, completed_at')
        .eq('user_id', userId!)
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        responses: (data.responses ?? {}) as Responses,
        overall_score: data.overall_score,
        stage_scores: data.stage_scores as Record<string, number> | null,
        zone_counts: data.zone_counts as Record<string, number> | null,
        completed_at: data.completed_at,
      };
    },
  });
}

/** Every completed assessment for the current user, most recent first. */
export function useResponseHistory(userId: string | undefined) {
  return useQuery<CompletedResponse[]>({
    queryKey: ['assessment-history', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('id, responses, overall_score, stage_scores, zone_counts, completed_at')
        .eq('user_id', userId!)
        .order('completed_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((r) => ({
        id: r.id,
        responses: (r.responses ?? {}) as Responses,
        overall_score: r.overall_score,
        stage_scores: r.stage_scores as Record<string, number> | null,
        zone_counts: r.zone_counts as Record<string, number> | null,
        completed_at: r.completed_at,
      }));
    },
  });
}

/** A single completed response by its id (RLS gates to owner). */
export function useResponseById(id: string | undefined) {
  return useQuery<CompletedResponse | null>({
    queryKey: ['assessment-response', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('id, responses, overall_score, stage_scores, zone_counts, completed_at')
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        responses: (data.responses ?? {}) as Responses,
        overall_score: data.overall_score,
        stage_scores: data.stage_scores as Record<string, number> | null,
        zone_counts: data.zone_counts as Record<string, number> | null,
        completed_at: data.completed_at,
      };
    },
  });
}

/**
 * Submit a completed assessment. Precomputes overall_score, per-stage
 * averages, and zone counts at save time — that way historical rows
 * stay interpretable even if we tweak the scoring rules later.
 */
export function useSubmitAssessmentResponse(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (responses: Responses) => {
      if (!userId) throw new Error('not signed in');
      const overall = overallAverage(responses);
      const stage_scores = allStageAverages(responses);
      const zc = overallZoneCounts(responses);
      const zone_counts = { red: zc.red, neutral: zc.neutral, blue: zc.blue, total: zc.total };

      const { data, error } = await supabase
        .from('assessment_responses')
        .insert({
          user_id: userId,
          responses,
          overall_score: Number(overall.toFixed(2)),
          stage_scores,
          zone_counts,
        })
        .select()
        .single();
      if (error) throw error;
      // Clear the draft now that a response is saved.
      await supabase.from('assessment_drafts').delete().eq('user_id', userId);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-latest', userId] });
      qc.invalidateQueries({ queryKey: ['assessment-draft', userId] });
      qc.invalidateQueries({ queryKey: ['assessment-history', userId] });
    },
  });
}

// ============================================================
// CYBHI Checklists — list / create / item state
// ============================================================

export interface ChecklistRow {
  id: string;
  org_id: string;
  name: string;
  pathway: 'A' | 'B' | 'C' | null;
  sub_pathway_c: 'C1' | 'C2' | 'C3' | null;
  scenarios: string[];
  archived: boolean;
  created_at: string;
  updated_at: string;
}

/** List the checklists owned by a workspace, most recent first. */
export function useChecklists(orgId: string | undefined) {
  return useQuery<ChecklistRow[]>({
    queryKey: ['checklists', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('checklists')
        .select('id, org_id, name, pathway, sub_pathway_c, scenarios, archived, created_at, updated_at')
        .eq('org_id', orgId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ChecklistRow[];
    },
  });
}

/** Single checklist by id (RLS gates to workspace members). */
export function useChecklist(checklistId: string | undefined) {
  return useQuery<ChecklistRow | null>({
    queryKey: ['checklist', checklistId],
    enabled: !!checklistId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('checklists')
        .select('id, org_id, name, pathway, sub_pathway_c, scenarios, archived, created_at, updated_at')
        .eq('id', checklistId!)
        .maybeSingle();
      if (error) throw error;
      return data as ChecklistRow | null;
    },
  });
}

export function useCreateChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      org_id: string;
      name: string;
      pathway: 'A' | 'B' | 'C';
      sub_pathway_c: 'C1' | 'C2' | 'C3' | null;
      scenarios: string[];
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('not signed in');
      const { data, error } = await supabase
        .from('checklists')
        .insert({
          org_id: args.org_id,
          name: args.name,
          pathway: args.pathway,
          sub_pathway_c: args.sub_pathway_c,
          scenarios: args.scenarios,
          created_by: user.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ChecklistRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['checklists', row.org_id] });
    },
  });
}

export interface ItemState {
  item_id: string;
  checked: boolean;
  notes: string | null;
  updated_at: string;
  updated_by: string;
}

/** All per-item states for a checklist, keyed by item_id. */
export function useChecklistItemStates(checklistId: string | undefined) {
  return useQuery<Record<string, ItemState>>({
    queryKey: ['checklist-items', checklistId],
    enabled: !!checklistId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('checklist_item_state')
        .select('item_id, checked, notes, updated_at, updated_by')
        .eq('checklist_id', checklistId!);
      if (error) throw error;
      const out: Record<string, ItemState> = {};
      for (const r of (data ?? []) as ItemState[]) out[r.item_id] = r;
      return out;
    },
  });
}

/**
 * Toggle a single item's `checked` state. Optimistic — flips the cache
 * immediately, then upserts the row server-side. Rolls back on failure.
 */
export function useToggleChecklistItem(checklistId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { item_id: string; checked: boolean }) => {
      if (!checklistId) throw new Error('no checklist');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('not signed in');
      const { error } = await supabase
        .from('checklist_item_state')
        .upsert(
          {
            checklist_id: checklistId,
            item_id: args.item_id,
            checked: args.checked,
            updated_by: user.id,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'checklist_id,item_id' },
        );
      if (error) throw error;
    },
    onMutate: async (args) => {
      if (!checklistId) return { prev: undefined };
      await qc.cancelQueries({ queryKey: ['checklist-items', checklistId] });
      const prev = qc.getQueryData<Record<string, ItemState>>([
        'checklist-items',
        checklistId,
      ]);
      qc.setQueryData<Record<string, ItemState>>(
        ['checklist-items', checklistId],
        (old) => ({
          ...(old ?? {}),
          [args.item_id]: {
            item_id: args.item_id,
            checked: args.checked,
            notes: old?.[args.item_id]?.notes ?? null,
            updated_at: new Date().toISOString(),
            updated_by: old?.[args.item_id]?.updated_by ?? '',
          },
        }),
      );
      return { prev };
    },
    onError: (_err, _args, ctx) => {
      if (ctx?.prev && checklistId) {
        qc.setQueryData(['checklist-items', checklistId], ctx.prev);
      }
    },
    onSettled: () => {
      if (checklistId) {
        qc.invalidateQueries({ queryKey: ['checklist-items', checklistId] });
      }
    },
  });
}

// ============================================================
// Founder-approval gate
// ============================================================

/** Lightweight self-check: am I approved to use CYBHI workspaces? */
export function useMyApprovalStatus(userId: string | undefined) {
  return useQuery<boolean>({
    queryKey: ['am-i-approved', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('am_i_approved');
      if (error) throw error;
      return !!data;
    },
  });
}

/** Self-check: am I the founder? Used to show the admin link. */
export function useIsAppAdmin(userId: string | undefined) {
  return useQuery<boolean>({
    queryKey: ['is-app-admin', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('is_app_admin');
      if (error) throw error;
      return !!data;
    },
  });
}

export interface PendingApproval {
  user_id: string;
  email: string;
  full_name: string | null;
  requested_at: string;
  email_confirmed_at: string | null;
}

/** Admin only: queue of users awaiting approval. */
export function usePendingApprovals(enabled: boolean) {
  return useQuery<PendingApproval[]>({
    queryKey: ['pending-approvals'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_pending_approvals');
      if (error) throw error;
      return (data ?? []) as PendingApproval[];
    },
  });
}

export function useApproveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.rpc('approve_user', { p_user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
}

export function useDeclineUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.rpc('decline_user', { p_user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
}

// ============================================================
// Workspace invites
// ============================================================

export interface InviteRow {
  id: string;
  org_id: string;
  email: string;
  role: OrgRole;
  token: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
}

/** Pending + recently-accepted invites for a workspace (admin RLS gates). */
export function useWorkspaceInvites(orgId: string | undefined) {
  return useQuery<InviteRow[]>({
    queryKey: ['workspace-invites', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('org_invites')
        .select('id, org_id, email, role, token, created_at, expires_at, accepted_at')
        .eq('org_id', orgId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as InviteRow[];
    },
  });
}

/** Create an invite (admin RLS gates). Client generates the token. */
export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { org_id: string; email: string; role?: OrgRole }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('not signed in');
      // 32-char URL-safe token; UUID without dashes works fine.
      const token = crypto.randomUUID().replace(/-/g, '');
      const { data, error } = await supabase
        .from('org_invites')
        .insert({
          org_id: args.org_id,
          email: args.email.trim().toLowerCase(),
          role: args.role ?? 'member',
          token,
          invited_by: user.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data as InviteRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['workspace-invites', row.org_id] });
    },
  });
}

/** Anonymous-friendly: look up an invite by its share token. */
export interface InviteLookup {
  org_id: string;
  org_name: string;
  role: OrgRole;
  email: string;
  expires_at: string;
  accepted_at: string | null;
}

export function useInviteLookup(token: string | undefined) {
  return useQuery<InviteLookup | null>({
    queryKey: ['invite-lookup', token],
    enabled: !!token,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('lookup_invite_by_token', {
        p_token: token!,
      });
      if (error) throw error;
      const row = (data as InviteLookup[] | null)?.[0] ?? null;
      return row;
    },
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await supabase.rpc('accept_invite', { p_token: token });
      if (error) throw error;
      return data as string; // org_id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-workspaces'] });
      qc.invalidateQueries({ queryKey: ['workspace-invites'] });
    },
  });
}

// ============================================================
// Workspace members (CYBHI)
// ============================================================

export function useWorkspaceMembers(orgId: string | undefined) {
  return useQuery<WorkspaceMember[]>({
    queryKey: ['workspace-members', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      // Embed profiles to get the full_name for display.
      const { data, error } = await supabase
        .from('org_members')
        .select('user_id, role, joined_at, profiles!inner(full_name)')
        .eq('org_id', orgId!)
        .order('joined_at', { ascending: true });

      if (error) throw error;

      type Row = {
        user_id: string;
        role: OrgRole;
        joined_at: string;
        profiles: { full_name: string | null };
      };

      return (data as unknown as Row[]).map((r) => ({
        user_id: r.user_id,
        role: r.role,
        joined_at: r.joined_at,
        full_name: r.profiles.full_name,
      }));
    },
  });
}
